import {
  checkIsAlreadyScannedEmptyNests,
  checkIsPlateRecognitionEnabled,
  clearAllCurrentScannedNestsData,
  getIsPlateAlreadyTrained,
  getPlatesTrainingStatusApi,
  registerCachedDetectionData,
  registerPlateLibAllEmptyNests,
  removePlateFromLibraryApi,
  scanSingleNestAndCacheData,
  SetFeedbackForRuntimePlateValidationApi,
  setPlateRecognitionEnabled,
} from 'api/plateRecognitionApi';
import FormattedHTMLMessage from 'components/FormattedHTMLMessage';
import {FastRobotErrorcode, Feedback, ModalType} from 'constants/dataEnum';
import {hideLoading, showLoading, showSnackbar} from 'stores/actions/appStateAction';
import {
  setAllPlatesScanned,
  setIsAlreadyScannedAllEmptyNests,
  setIsIncompatibleVisionVersion,
  setIsPlateAlreadyTrained,
  setIsPlateEditStateChanged,
  setIsPlateRecognitionEnabled,
} from 'stores/actions/plateRecognitionAction';
import getTemporaryState from 'stores/selectors/temporaryStateSelector';
import {
  selectIsAlreadyScannedAllEmptyNests,
  selectIsPlateRecognitionEnabled,
} from 'stores/selectors/plateRecognitionSelectors';
import {showModalType} from 'stores/actions/typedModalAction';
import {resolveIntl} from './commonBusiness';
import {resumeProtocol} from './instrumentBusiness';
import {showUntrainedPlatesModal} from './commonModalBusiness';
// eslint-disable-next-line import/no-cycle
import {runApiResponseHandler} from './runtimeBusiness';

const intl = resolveIntl();

export const initializePlateRecognitionState = () => async (dispatch) => {
  try {
    const [isPlateRecognitionEnabled, isAlreadyScannedAllEmptyNests] = await Promise.all([
      checkIsPlateRecognitionEnabled(),
      checkIsAlreadyScannedEmptyNests(),
    ]);

    dispatch(setIsPlateRecognitionEnabled(isPlateRecognitionEnabled));
    dispatch(setIsAlreadyScannedAllEmptyNests(isAlreadyScannedAllEmptyNests.status));
  } catch (error) {
    console.error('Error initializing plate recognition state:', error);
  }
};

export const getIsAlreadyScannedAllEmptyNestsState = () => async (dispatch, getState) => {
  const isAlreadyScannedAllEmptyNests = selectIsAlreadyScannedAllEmptyNests(getState());
  const {status} = await checkIsAlreadyScannedEmptyNests();
  if (status !== isAlreadyScannedAllEmptyNests) {
    dispatch(setIsAlreadyScannedAllEmptyNests(status));
  }
  return status;
};

export const setPlateRecognitionState = (isEnabled) => async (dispatch, getState) => {
  const isAlreadyScannedAllEmptyNests = selectIsAlreadyScannedAllEmptyNests(getState());
  const status = isEnabled ? 'on' : 'off';
  let fastErrorCode;

  dispatch(showLoading(<FormattedHTMLMessage id="modal.plateRecognition.loading" values={{status}} />));

  try {
    if (!isAlreadyScannedAllEmptyNests) {
      fastErrorCode = await registerPlateLibAllEmptyNests();
      if (dispatch(handlePlateRecognitionError(fastErrorCode))) {
        dispatch(hideLoading());
        return;
      }

      dispatch(setIsAlreadyScannedAllEmptyNests(true));
    }

    fastErrorCode = await setPlateRecognitionEnabled(isEnabled);
    if (dispatch(handlePlateRecognitionError(fastErrorCode))) {
      dispatch(hideLoading());
      return;
    }

    dispatch(setIsPlateRecognitionEnabled(isEnabled));

    dispatch(hideLoading());
    dispatch(
      showSnackbar(Feedback.INFO, <FormattedHTMLMessage id="modal.plateRecognition.success" values={{status}} />),
    );
  } catch (error) {
    console.error('Error setting plate recognition state:', error);
  }
};

const handlePlateRecognitionError = (errorCode) => (dispatch) => {
  if (errorCode === FastRobotErrorcode.NoError) return false;

  dispatch(
    showSnackbar(
      Feedback.SUCCESS,
      intl.formatMessage({
        id: 'modal.plateRecognition.error',
      }),
    ),
  );

  dispatch(runApiResponseHandler(errorCode));

  return true;
};

export const scanPlate = (nestId) => async (dispatch) => {
  dispatch(showLoading(intl.formatMessage({id: 'plateTypeEditorContainer.plateRecognition.plateScan.loading'})));

  const fastErrorCode = await scanSingleNestAndCacheData(nestId);
  if (fastErrorCode !== FastRobotErrorcode.NoError) {
    dispatch(
      showSnackbar(
        Feedback.ERROR,
        <FormattedHTMLMessage id="plateTypeEditorContainer.plateRecognition.plateScan.error" />,
      ),
    );

    if (fastErrorCode !== FastRobotErrorcode.LABWARE_NOT_DETECTED) {
      dispatch(runApiResponseHandler(fastErrorCode));
    }

    dispatch(hideLoading());
    return fastErrorCode;
  }

  dispatch(hideLoading());
  dispatch(
    showSnackbar(
      Feedback.SUCCESS,
      intl.formatMessage({id: 'plateTypeEditorContainer.plateRecognition.plateScan.success'}),
    ),
  );
  return fastErrorCode;
};

export const registerCachedAndClearScannedData =
  (labwareId, isEdit = false) =>
  async (dispatch) => {
    await registerCachedDetectionData(labwareId);
    await dispatch(resetScannedPlatesState(isEdit));
  };

export const resetScannedPlatesState =
  (isEdit = false) =>
  async (dispatch) => {
    if (isEdit) {
      dispatch(setIsPlateAlreadyTrained(false));
      dispatch(setIsPlateEditStateChanged(false));
    }
    dispatch(setAllPlatesScanned(false));
    await clearAllCurrentScannedNestsData();
  };

export const isPlateAlreadyTrained = () => async (dispatch, getState) => {
  const state = getState();
  const tempState = getTemporaryState('plateTypeEditorState')(state);

  if (!tempState?.id) {
    dispatch(setIsPlateAlreadyTrained(false));
    return false;
  }

  const isTrained = await getIsPlateAlreadyTrained(tempState.id);
  dispatch(setIsPlateAlreadyTrained(isTrained));
  return isTrained;
};

export const handleInvalidPlateRecognition =
  (isValidate, falseDetectionNestIds, reScanNestsIds) => async (dispatch) => {
    if (isValidate) {
      await SetFeedbackForRuntimePlateValidationApi(falseDetectionNestIds, reScanNestsIds);
      dispatch(resumeProtocol());
      return;
    }

    dispatch(resumeProtocol(true));
  };

export const checkNestForUntrainedPlates = (startRunProtocol) => async (dispatch, getState) => {
  const {labwareState} = getState();

  const plateIds = Object.values(labwareState)
    .map((item) => item.IDPrefix)
    .filter(Boolean);

  const trainingStatus = await getPlatesTrainingStatusApi(plateIds);

  const untrainedNestIds = Object.entries(labwareState)
    .filter(([, {IDPrefix}]) => IDPrefix && trainingStatus[IDPrefix] === false)
    .map(([nestId]) => Number(nestId));

  if (untrainedNestIds.length === 0) {
    startRunProtocol();
    return;
  }

  dispatch(showUntrainedPlatesModal(untrainedNestIds, startRunProtocol));
};

export const handleIncompatibleVisionVersionError = () => async (dispatch, getState) => {
  const isPlateRecognitionEnabled = selectIsPlateRecognitionEnabled(getState());
  if (isPlateRecognitionEnabled) {
    setPlateRecognitionState(false);
  }
  dispatch(setIsIncompatibleVisionVersion(true));
  dispatch(showModalType(ModalType.INCOMPATIBLE_VISION_VERSION));
};

export const removePlatesFromLibrary = (ids, labwareNotDeletedList) => async () => {
  const notDeletedIds = labwareNotDeletedList.map((item) => item.labwareId);
  const deletedIds = ids.filter((id) => !notDeletedIds.includes(id));
  await removePlateFromLibraryApi(deletedIds);
};
